import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CampaignAttendanceMode,
  CampaignItem,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  Usuario,
  WhatsAppAccount,
} from '../../types';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { buildFullCampaignDispatchPreview } from '../CampaignManager/campaignPreview';
import { formatWhatsappSelectLabel } from '../../utils/whatsappAccountLabels';
import {
  CampaignSourceManager,
  type CampaignSourceManagerHandle,
  type CampaignSourceSelectionTotals,
} from '../CampaignSourceManager/CampaignSourceManager';
import './CampaignWizard.css';

const DEFAULT_PRINCIPAL_CALL_INTRO = 'Clique no link para falar com nosso consultor:';

const USER_ROLE_LABELS: Record<Usuario['role'], string> = {
  owner: 'Owner',
  admin: 'Admin',
  gerente: 'Gerente',
  atendente: 'Atendente',
  super_admin: 'Super Admin',
};

const USER_ROLE_PRIORITY: Record<Usuario['role'], number> = {
  owner: 0,
  admin: 1,
  gerente: 2,
  atendente: 3,
  super_admin: 4,
};

const ROLES_MAY_DELEGATE: Usuario['role'][] = ['owner', 'admin', 'gerente', 'super_admin'];

const STEPS = [
  { id: 1, label: 'Dados da campanha' },
  { id: 2, label: 'Público' },
  { id: 3, label: 'Atendimento e disparo' },
  { id: 4, label: 'Revisão' },
] as const;

export interface CampaignWizardProps {
  onClose: () => void;
  /** Ao concluir envio ou escolher ver kanban */
  onComplete: (result: { campaignId: string; goToKanban: boolean }) => void;
  initialCampaignId?: string | null;
}

export const CampaignWizard: React.FC<CampaignWizardProps> = ({
  onClose,
  onComplete,
  initialCampaignId,
}) => {
  const { usuario, organization } = useAuth();
  const [step, setStep] = useState(1);
  const [campaignId, setCampaignId] = useState<string | null>(initialCampaignId || null);
  const [loading, setLoading] = useState(!!initialCampaignId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [nome, setNome] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [createDispatchWhatsappAccountId, setCreateDispatchWhatsappAccountId] = useState('');
  const [createPrincipalBlock, setCreatePrincipalBlock] = useState(false);
  const [createPrincipalIntro, setCreatePrincipalIntro] = useState('');
  // `createPrincipalUsuarioId` é AUTO-DERIVADO dos USUÁRIOS RESPONSÁVEIS do
  // step 3 (não mais editado manualmente no step 1). Mantido como state pra
  // compat com payload `principalWhatsappUsuarioId` da Campaign.
  const [createPrincipalUsuarioId, setCreatePrincipalUsuarioId] = useState('');
  // add-campaign-lead-assignment: USUÁRIOS RESPONSÁVEIS no step 3.
  // 0 marcados = legacy (dono da WhatsApp account vira atendente);
  // 1 marcado = assignmentMode='single' + auto-deriva
  //   principalWhatsappUsuarioId (link wa.me/... do "Ligue para");
  // 2+ marcados = MVP atual rejeita com aviso inline.
  const [createAssignedUserIds, setCreateAssignedUserIds] = useState<string[]>([]);
  const [createDelegateManagementToPrincipalMember, setCreateDelegateManagementToPrincipalMember] =
    useState(false);
  const [useGlobalUnsubscribeConfig, setUseGlobalUnsubscribeConfig] = useState(true);
  const [createUnsubscribeFooterTemplate, setCreateUnsubscribeFooterTemplate] = useState('');
  const [createAllowActivePosVendasDispatch, setCreateAllowActivePosVendasDispatch] = useState(false);
  const [createAttendanceMode, setCreateAttendanceMode] = useState<CampaignAttendanceMode>('human_kanban');

  const [waMembers, setWaMembers] = useState<Usuario[]>([]);
  const [waMembersLoadFailed, setWaMembersLoadFailed] = useState(false);
  const [waAccounts, setWaAccounts] = useState<WhatsAppAccount[]>([]);

  const [intervaloMinutos, setIntervaloMinutos] = useState(5);
  const [horaInicio, setHoraInicio] = useState('09:00');
  const [horaFim, setHoraFim] = useState('18:00');
  const [maxDisparosPorNumero, setMaxDisparosPorNumero] = useState(100);

  const [campaignItems, setCampaignItems] = useState<CampaignItem[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const sourceManagerRef = useRef<CampaignSourceManagerHandle>(null);
  const [wizardStep2Totals, setWizardStep2Totals] = useState<CampaignSourceSelectionTotals>({
    total: 0,
    perdidos: 0,
    inativos: 0,
    parceiros: 0,
    csv: 0,
  });

  const onWizardSelectionTotals = useCallback((t: CampaignSourceSelectionTotals) => {
    setWizardStep2Totals(t);
  }, []);

  const waMembersForPicker = useMemo(() => {
    const base = [...waMembers];
    const self = usuario;
    const selfPrincipal = self && (self.whatsappAccountId || '').trim();
    if (!self?.usuarioId || !selfPrincipal) {
      return base.sort((a, b) => {
        const byRole = USER_ROLE_PRIORITY[a.role] - USER_ROLE_PRIORITY[b.role];
        if (byRole !== 0) return byRole;
        return a.nome.localeCompare(b.nome);
      });
    }
    const already = base.some((u) => u.usuarioId === self.usuarioId);
    const mergeSelf =
      (self.role === 'atendente' && !already) || (waMembersLoadFailed && !already);
    const withSelf = mergeSelf ? [self, ...base] : base;
    return withSelf.sort((a, b) => {
      const byRole = USER_ROLE_PRIORITY[a.role] - USER_ROLE_PRIORITY[b.role];
      if (byRole !== 0) return byRole;
      return a.nome.localeCompare(b.nome);
    });
  }, [waMembers, usuario, waMembersLoadFailed]);

  const waMembersWithPrincipal = useMemo(
    () => waMembersForPicker.filter((u) => (u.whatsappAccountId || '').trim()),
    [waMembersForPicker]
  );

  const selectableDispatchAccounts = useMemo(() => {
    if (!usuario) return waAccounts.filter((a) => a.status === 'conectado');
    if (['owner', 'admin', 'gerente', 'super_admin'].includes(usuario.role)) {
      return waAccounts.filter((a) => a.status === 'conectado');
    }
    const ids = [usuario.whatsappAccountId, usuario.whatsappAccountIdCampanha].filter(Boolean) as string[];
    return waAccounts.filter((a) => a.status === 'conectado' && ids.includes(a.accountId));
  }, [usuario, waAccounts]);

  const createPrincipalSelectedMember = useMemo(() => {
    const id = createPrincipalUsuarioId.trim();
    if (!id) return undefined;
    return waMembersForPicker.find((u) => u.usuarioId === id);
  }, [createPrincipalUsuarioId, waMembersForPicker]);

  // add-campaign-lead-assignment: auto-deriva `createPrincipalUsuarioId`
  // (membro do "Ligue para") do primeiro usuário marcado no step 3 (USUÁRIOS
  // RESPONSÁVEIS). Quando admin desmarca tudo, mantém o valor anterior pra
  // não estourar validações de "Adicionar Ligue para" ativo.
  useEffect(() => {
    if (createAssignedUserIds.length >= 1) {
      const first = createAssignedUserIds[0];
      if (first && first !== createPrincipalUsuarioId) {
        setCreatePrincipalUsuarioId(first);
      }
    }
  }, [createAssignedUserIds, createPrincipalUsuarioId]);

  // Lista de usuários ativos da org elegíveis pra atendimento (qualquer
  // role com whatsappAccountId). Mesma lista do antigo dropdown.
  const orgActiveUsersForAssignment = waMembersForPicker;

  const showCreateDelegateCheckbox =
    !!usuario &&
    ROLES_MAY_DELEGATE.includes(usuario.role) &&
    createPrincipalBlock &&
    createPrincipalSelectedMember?.role === 'atendente';

  const allowedAccountIds = useMemo(() => {
    if (!usuario) return new Set(selectableDispatchAccounts.map((a) => a.accountId));
    if (['owner', 'admin', 'gerente', 'super_admin'].includes(usuario.role)) {
      return new Set(selectableDispatchAccounts.map((a) => a.accountId));
    }
    const ids = [usuario.whatsappAccountId, usuario.whatsappAccountIdCampanha].filter(Boolean) as string[];
    return new Set(ids);
  }, [usuario, selectableDispatchAccounts]);

  const createFullMessagePreview = useMemo(
    () =>
      buildFullCampaignDispatchPreview({
        mensagem,
        principalBlockEnabled: createPrincipalBlock,
        principalIntro: createPrincipalIntro,
        principalUsuarioId: createPrincipalUsuarioId,
        useGlobalUnsubscribe: useGlobalUnsubscribeConfig,
        unsubscribeFooterTemplate: createUnsubscribeFooterTemplate,
      }),
    [
      mensagem,
      createPrincipalBlock,
      createPrincipalIntro,
      createPrincipalUsuarioId,
      useGlobalUnsubscribeConfig,
      createUnsubscribeFooterTemplate,
    ]
  );

  /** Resumo lateral + revisão: mesmo formato que os mockups (🤖 IA vs Manual). */
  const attendanceSummaryPill = useMemo(
    () =>
      createAttendanceMode === 'ai_funnel' ? (
        <span
          className="campaign-wizard-ia-pill campaign-wizard-ia-pill--emoji"
          title="Atendimento por IA (funil)"
        >
          <span aria-hidden>🤖</span>
          <span>IA</span>
        </span>
      ) : (
        <span className="campaign-wizard-manual-pill" title="Atendimento manual (Kanban humano)">
          Manual
        </span>
      ),
    [createAttendanceMode]
  );

  const reviewFontesResumo = useMemo(() => {
    const parts: string[] = [];
    if (wizardStep2Totals.perdidos > 0) {
      parts.push(`perdidos: ${wizardStep2Totals.perdidos.toLocaleString('pt-BR')}`);
    }
    if (wizardStep2Totals.inativos > 0) {
      parts.push(`inativos: ${wizardStep2Totals.inativos.toLocaleString('pt-BR')}`);
    }
    if (wizardStep2Totals.parceiros > 0) {
      parts.push(`parceiros: ${wizardStep2Totals.parceiros.toLocaleString('pt-BR')}`);
    }
    if (wizardStep2Totals.csv > 0) {
      parts.push(`csv: ${wizardStep2Totals.csv.toLocaleString('pt-BR')}`);
    }
    return parts.length > 0 ? parts.join(' · ') : '—';
  }, [wizardStep2Totals]);

  const loadItems = useCallback(async (cid: string) => {
    try {
      const items = await apiService.listCampaignItems({ campaignId: cid });
      setCampaignItems(items);
    } catch {
      setCampaignItems([]);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const accts = await apiService.getWhatsAppAccounts();
        setWaAccounts(accts.filter((a) => a.status === 'conectado'));
      } catch {
        setWaAccounts([]);
      }
      try {
        const list = await apiService.getUsuarios();
        setWaMembers(
          list
            .filter((u) => u.ativo !== false)
            .sort((a, b) => {
              const byRole = USER_ROLE_PRIORITY[a.role] - USER_ROLE_PRIORITY[b.role];
              if (byRole !== 0) return byRole;
              return a.nome.localeCompare(b.nome);
            })
        );
        setWaMembersLoadFailed(false);
      } catch {
        setWaMembers([]);
        setWaMembersLoadFailed(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!initialCampaignId) {
      setLoading(false);
      return;
    }
    void (async () => {
      setLoading(true);
      try {
        const c = await apiService.getCampaign(initialCampaignId);
        setCampaignId(c.campaignId);
        setNome(c.nome);
        setMensagem(c.mensagem || '');
        const cw = (c.whatsappAccountId || '').trim();
        if (cw && selectableDispatchAccounts.some((a) => a.accountId === cw)) {
          setCreateDispatchWhatsappAccountId(cw);
        }
        const uid = (c.principalWhatsappUsuarioId || '').trim();
        const blockOn = c.principalCallBlockEnabled === true || (uid.length > 0 && c.principalCallBlockEnabled !== false);
        setCreatePrincipalBlock(!!blockOn);
        setCreatePrincipalUsuarioId(uid);
        setCreatePrincipalIntro(
          blockOn ? (c.principalCallIntroText || '').trim() || DEFAULT_PRINCIPAL_CALL_INTRO : ''
        );
        // add-campaign-lead-assignment: carrega lista de USUÁRIOS RESPONSÁVEIS
        // do step 3 a partir de assignedAtendentes (MVP single) OU fallback
        // pro principalWhatsappUsuarioId (campanha legacy sem assignmentMode).
        if (c.assignmentMode === 'single' && c.assignedAtendentes?.length) {
          setCreateAssignedUserIds(
            c.assignedAtendentes.map((a) => (a.usuarioId || '').trim()).filter(Boolean)
          );
        } else if (uid) {
          setCreateAssignedUserIds([uid]);
        } else {
          setCreateAssignedUserIds([]);
        }
        setUseGlobalUnsubscribeConfig(c.unsubscribeUseGlobal !== false);
        setCreateUnsubscribeFooterTemplate(c.unsubscribeFooterTemplate || '');
        setCreateDelegateManagementToPrincipalMember(!!c.delegateManagementToPrincipalMember);
        setCreateAllowActivePosVendasDispatch(!!c.allowActivePosVendasDispatch);
        if (
          c.defaultDisparoIntervaloMinutos != null &&
          Number.isFinite(c.defaultDisparoIntervaloMinutos) &&
          c.defaultDisparoIntervaloMinutos >= 1
        ) {
          setIntervaloMinutos(c.defaultDisparoIntervaloMinutos);
        }
        if (
          c.defaultDisparoMaxDisparosPorNumero != null &&
          Number.isFinite(c.defaultDisparoMaxDisparosPorNumero) &&
          c.defaultDisparoMaxDisparosPorNumero >= 1
        ) {
          setMaxDisparosPorNumero(c.defaultDisparoMaxDisparosPorNumero);
        }
        if (c.defaultDisparoHoraInicio && /^\d{1,2}:\d{2}$/.test(c.defaultDisparoHoraInicio.trim())) {
          setHoraInicio(c.defaultDisparoHoraInicio.trim());
        }
        if (c.defaultDisparoHoraFim && /^\d{1,2}:\d{2}$/.test(c.defaultDisparoHoraFim.trim())) {
          setHoraFim(c.defaultDisparoHoraFim.trim());
        }
        setCreateAttendanceMode(c.attendanceMode || 'human_kanban');
        await loadItems(c.campaignId);
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Erro ao carregar campanha');
      } finally {
        setLoading(false);
      }
    })();
  }, [initialCampaignId, selectableDispatchAccounts, loadItems]);

  useEffect(() => {
    if (campaignId) void loadItems(campaignId);
  }, [campaignId, step, loadItems]);

  useEffect(() => {
    if (selectableDispatchAccounts.length === 1 && !createDispatchWhatsappAccountId) {
      setCreateDispatchWhatsappAccountId(selectableDispatchAccounts[0].accountId);
    }
  }, [selectableDispatchAccounts, createDispatchWhatsappAccountId]);

  function buildStep1Payload(): CreateCampaignRequest {
    const dispatchWa = createDispatchWhatsappAccountId.trim();
    // add-campaign-lead-assignment (MVP single only): USUÁRIOS RESPONSÁVEIS
    // do step 3. 1 marcado = single; 0 = legacy; 2+ = bloqueado em goNext().
    const assignedSingle = createAssignedUserIds.length === 1
      ? createAssignedUserIds[0].trim()
      : '';
    return {
      nome: nome.trim() || 'Nova campanha',
      mensagem: mensagem.trim(),
      ...(dispatchWa ? { whatsappAccountId: dispatchWa } : {}),
      ...(createPrincipalBlock
        ? {
            principalCallBlockEnabled: true,
            principalWhatsappUsuarioId: createPrincipalUsuarioId.trim(),
            principalCallIntroText: createPrincipalIntro.trim(),
            delegateManagementToPrincipalMember:
              showCreateDelegateCheckbox && createDelegateManagementToPrincipalMember,
          }
        : {
            principalCallBlockEnabled: false,
            delegateManagementToPrincipalMember: false,
          }),
      ...(assignedSingle
        ? {
            assignmentMode: 'single' as const,
            assignedAtendentes: [{ usuarioId: assignedSingle }],
          }
        : {}),
      ...(useGlobalUnsubscribeConfig
        ? { unsubscribeUseGlobal: true }
        : {
            unsubscribeUseGlobal: false,
            unsubscribeOverrideEnabled: true,
            unsubscribeBaseUrl: '',
            unsubscribeFooterTemplate: createUnsubscribeFooterTemplate,
          }),
      ...(createAllowActivePosVendasDispatch ? { allowActivePosVendasDispatch: true } : {}),
      attendanceMode: createAttendanceMode,
    };
  }

  function buildDispatchDefaultsPayload(): Pick<
    UpdateCampaignRequest,
    | 'defaultDisparoIntervaloMinutos'
    | 'defaultDisparoMaxDisparosPorNumero'
    | 'defaultDisparoHoraInicio'
    | 'defaultDisparoHoraFim'
  > {
    return {
      defaultDisparoIntervaloMinutos: intervaloMinutos,
      defaultDisparoMaxDisparosPorNumero: maxDisparosPorNumero,
      defaultDisparoHoraInicio: horaInicio,
      defaultDisparoHoraFim: horaFim,
    };
  }

  async function ensureCampaignId(): Promise<string> {
    if (campaignId) return campaignId;
    const body: CreateCampaignRequest = {
      status: 'rascunho',
      ...buildStep1Payload(),
    };
    const c = await apiService.createCampaign(body);
    setCampaignId(c.campaignId);
    return c.campaignId;
  }

  async function saveDraft() {
    setError(null);
    try {
      setSaving(true);
      const id = await ensureCampaignId();
      const patch: UpdateCampaignRequest = { ...buildStep1Payload() };
      if (step >= 3) {
        Object.assign(patch, buildDispatchDefaultsPayload());
      }
      await apiService.patchCampaign(id, patch);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao salvar rascunho');
    } finally {
      setSaving(false);
    }
  }

  async function goNext() {
    setError(null);
    if (step === 1) {
      const tn = nome.trim();
      if (!tn) {
        setError('Informe o nome da campanha.');
        return;
      }
      if (!mensagem.trim()) {
        setError('Informe a mensagem da campanha.');
        return;
      }
      try {
        setSaving(true);
        const id = await ensureCampaignId();
        await apiService.patchCampaign(id, { ...buildStep1Payload() });
        setStep(2);
      } catch (e: any) {
        setError(e?.response?.data?.message || e?.message || 'Erro ao avançar');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (step === 2) {
      if (!campaignId) {
        setError('Campanha inválida.');
        return;
      }
      try {
        setSaving(true);
        const result = await sourceManagerRef.current?.submitSelectionsToCampaign();
        if (!result?.ok) {
          if (result?.errorCode === 'empty') {
            setError('Selecione pelo menos um contacto para incluir na campanha.');
          } else if (result?.errorMessage) {
            setError(result.errorMessage);
          } else {
            setError('Não foi possível adicionar os contactos à campanha.');
          }
          return;
        }
        await loadItems(campaignId);
        setStep(3);
      } catch (e: any) {
        setError(e?.response?.data?.message || e?.message || 'Erro ao adicionar contactos à campanha.');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (step === 3) {
      if (intervaloMinutos < 1) {
        setError('Intervalo mínimo é 1 minuto.');
        return;
      }
      if (maxDisparosPorNumero < 1) {
        setError('O máximo de envios por rodada deve ser pelo menos 1.');
        return;
      }
      if (createAssignedUserIds.length > 1) {
        setError(
          'MVP atual aceita só 1 atendente em Usuários responsáveis. Distribuição entre vários virá na próxima versão.'
        );
        return;
      }
      if (createPrincipalBlock && createAssignedUserIds.length === 0) {
        setError(
          'Marque pelo menos 1 usuário responsável — o WhatsApp dele gera o link "Ligue para" do step 1.'
        );
        return;
      }
      if (createPrincipalBlock && !createPrincipalIntro.trim()) {
        setError(
          'Preencha o texto introdutório do bloco "Adicionar Ligue para" no step 1.'
        );
        return;
      }
      try {
        setSaving(true);
        const id = await ensureCampaignId();
        // Persistir assignedAtendentes do step 3 (junto com defaults de disparo).
        await apiService.patchCampaign(id, {
          ...buildDispatchDefaultsPayload(),
          ...(createAssignedUserIds.length === 1
            ? {
                assignmentMode: 'single' as const,
                assignedAtendentes: [{ usuarioId: createAssignedUserIds[0] }],
                principalWhatsappUsuarioId: createAssignedUserIds[0],
              }
            : createAssignedUserIds.length === 0
              ? {
                  assignmentMode: null,
                  assignedAtendentes: null,
                }
              : {}),
        });
        setStep(4);
      } catch (e: any) {
        setError(e?.response?.data?.message || e?.message || 'Erro ao guardar configuração do disparo');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (step === 4) {
      await submitFinalize();
    }
  }

  async function submitFinalize() {
    if (!campaignId) {
      setError('Campanha inválida.');
      return;
    }
    const dispatchWa = createDispatchWhatsappAccountId.trim();
    if (!mensagem.trim()) {
      setError('Mensagem da campanha é obrigatória para enviar.');
      return;
    }
    if (!dispatchWa) {
      setError('Selecione a linha padrão WhatsApp da campanha.');
      return;
    }
    if (createPrincipalBlock && (!createPrincipalUsuarioId.trim() || !createPrincipalIntro.trim())) {
      setError('Complete o bloco «Ligue para» ou desative-o.');
      return;
    }
    const waLine = createDispatchWhatsappAccountId.trim();
    if (!waLine || !allowedAccountIds.has(waLine)) {
      setError('Selecione a linha WhatsApp da campanha (passo 1).');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await apiService.patchCampaign(campaignId, {
        status: 'pendente',
        ...buildStep1Payload(),
        whatsappAccountId: waLine,
        ...buildDispatchDefaultsPayload(),
      });
      setShowConfirm(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao ativar campanha');
    } finally {
      setSaving(false);
    }
  }

  function goBack() {
    setError(null);
    if (step > 1) setStep((s) => s - 1);
  }

  const leadCount = campaignItems.length;

  if (loading) {
    return (
      <div className="campaign-wizard">
        <p>Carregando…</p>
      </div>
    );
  }

  if (showConfirm && campaignId) {
    return (
      <div className="campaign-wizard">
        <div className="campaign-wizard-card campaign-wizard-confirm">
          <h3>Campanha ativada com sucesso</h3>
          <p>
            Aparece agora na coluna <strong>Pendente</strong> do Kanban de execução. A fila de disparo só é criada ao
            clicar em <strong>Play</strong> no cartão (não é possível iniciar se outra campanha já estiver a usar a
            mesma linha WhatsApp).
          </p>
          <div className="campaign-wizard-confirm-actions">
            <button type="button" className="btn btn-primary" onClick={() => onComplete({ campaignId, goToKanban: true })}>
              Ver execução no kanban
            </button>
            <button type="button" className="btn btn-outline" onClick={() => onClose()}>
              Fechar
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setShowConfirm(false);
                setCampaignId(null);
                setStep(1);
                setNome('');
                setMensagem('');
                setCreateDispatchWhatsappAccountId(
                  selectableDispatchAccounts.length === 1 ? selectableDispatchAccounts[0].accountId : ''
                );
                setIntervaloMinutos(5);
                setHoraInicio('09:00');
                setHoraFim('18:00');
                setMaxDisparosPorNumero(100);
                setCreatePrincipalBlock(false);
                setCreatePrincipalIntro('');
                setCreatePrincipalUsuarioId('');
                setCreateDelegateManagementToPrincipalMember(false);
                setUseGlobalUnsubscribeConfig(true);
                setCreateUnsubscribeFooterTemplate('');
                setCreateAllowActivePosVendasDispatch(false);
                setCreateAttendanceMode('human_kanban');
                setCampaignItems([]);
              }}
            >
              Nova campanha
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-wizard">
      <div className="campaign-wizard-top">
        <div>
          <h2>Nova campanha</h2>
          <p className="text-muted">
            {step === 1 && 'Configure os dados básicos da sua campanha'}
            {step === 2 && 'Selecione o público que receberá a campanha'}
            {step === 3 &&
              'Tipo de atendimento (manual ou IA), horários e limites do disparo — a linha WhatsApp é definida no passo 1'}
            {step === 4 && 'Revise todos os dados antes de enviar para execução'}
          </p>
        </div>
        <div className="campaign-wizard-actions-right">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn btn-outline" disabled={saving} onClick={() => void saveDraft()}>
            Salvar rascunho
          </button>
        </div>
      </div>

      <ol className="campaign-wizard-track" aria-label="Passos do assistente">
        {STEPS.map((s, i) => {
          const done = step > s.id;
          const active = step === s.id;
          const prevDone = i > 0 ? step > STEPS[i - 1].id : false;
          return (
            <li key={s.id} className="campaign-wizard-track-step">
              <div className="campaign-wizard-track-top">
                {i > 0 ? (
                  <div
                    className={`campaign-wizard-track-line campaign-wizard-track-line--before ${prevDone ? 'done' : ''}`}
                    aria-hidden
                  />
                ) : (
                  <div className="campaign-wizard-track-line-spacer" aria-hidden />
                )}
                <span
                  className={`campaign-wizard-track-circle ${done ? 'done' : ''} ${active ? 'active' : ''}`}
                  aria-current={active ? 'step' : undefined}
                >
                  {done ? '✓' : s.id}
                </span>
                {i < STEPS.length - 1 ? (
                  <div
                    className={`campaign-wizard-track-line campaign-wizard-track-line--after ${done ? 'done' : ''}`}
                    aria-hidden
                  />
                ) : (
                  <div className="campaign-wizard-track-line-spacer" aria-hidden />
                )}
              </div>
              <span className={`campaign-wizard-track-label ${active ? 'active' : ''}`}>{s.label}</span>
            </li>
          );
        })}
      </ol>

      {error && <div className="campaign-error">{error}</div>}

      {step === 1 && (
        <div className="campaign-wizard-layout">
          <div className="campaign-wizard-card">
            <div className="campaign-form-group">
              <label htmlFor="wiz-nome">Nome da campanha</label>
              <input
                id="wiz-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Campanha de Retomada Q1 2026"
                disabled={saving}
              />
            </div>
            <div className="campaign-form-group">
              <label htmlFor="wiz-msg">Mensagem da campanha</label>
              <textarea
                id="wiz-msg"
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                rows={5}
                placeholder="Digite a mensagem que será enviada aos leads…"
                disabled={saving}
              />
            </div>
            <div className="campaign-form-group">
              <label htmlFor="wiz-wa">Linha padrão WhatsApp</label>
              <select
                id="wiz-wa"
                value={createDispatchWhatsappAccountId}
                onChange={(e) => setCreateDispatchWhatsappAccountId(e.target.value)}
                disabled={saving || selectableDispatchAccounts.length === 0}
              >
                <option value="">Selecione uma linha</option>
                {selectableDispatchAccounts.map((a) => (
                  <option key={a.accountId} value={a.accountId}>
                    {formatWhatsappSelectLabel(a, waMembersForPicker)}
                  </option>
                ))}
              </select>
            </div>
            <div className="campaign-form-group">
              <label className="campaign-delegate-management-row">
                <input
                  type="checkbox"
                  checked={createAllowActivePosVendasDispatch}
                  onChange={(e) => setCreateAllowActivePosVendasDispatch(e.target.checked)}
                  disabled={saving}
                />
                <span>Permitir disparo para números já em pós-vendas ativo na organização</span>
              </label>
            </div>
            <div className="campaign-form-group">
              <fieldset className="campaign-unsub-fieldset" disabled={saving}>
                <legend className="campaign-unsub-legend">Ligue para</legend>
                <label className="campaign-unsub-label-row">
                  <input
                    type="checkbox"
                    checked={createPrincipalBlock}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setCreatePrincipalBlock(on);
                      if (on && !createPrincipalIntro.trim()) {
                        setCreatePrincipalIntro(DEFAULT_PRINCIPAL_CALL_INTRO);
                      }
                      if (!on) {
                        setCreatePrincipalIntro('');
                        setCreatePrincipalUsuarioId('');
                      }
                    }}
                  />
                  <span>Adicionar Ligue para</span>
                </label>
              </fieldset>
            </div>
            {createPrincipalBlock && (
              <div className="campaign-form-group">
                <label htmlFor="wiz-pr-intro">Texto antes do link</label>
                <textarea
                  id="wiz-pr-intro"
                  value={createPrincipalIntro}
                  onChange={(e) => setCreatePrincipalIntro(e.target.value)}
                  rows={2}
                  disabled={saving}
                />
                {/* add-campaign-lead-assignment: dropdown manual de "Membro
                  (WhatsApp principal)" removido — auto-derivado do primeiro
                  USUÁRIO RESPONSÁVEL marcado no step 3. Validação real
                  acontece no goNext do step 3. */}
                {showCreateDelegateCheckbox ? (
                  <label className="campaign-delegate-management-row">
                    <input
                      type="checkbox"
                      checked={createDelegateManagementToPrincipalMember}
                      onChange={(e) => setCreateDelegateManagementToPrincipalMember(e.target.checked)}
                      disabled={saving}
                    />
                    <span>Permitir disparo e Kanban ao atendente do «Ligue para» (após haver contactos)</span>
                  </label>
                ) : null}
              </div>
            )}
            {/* add-campaign-lead-assignment: dropdown "Atendente designado"
              removido — atendente é derivado dos USUÁRIOS RESPONSÁVEIS do
              step 3. */}
            <div className="campaign-form-group">
              <fieldset className="campaign-unsub-fieldset" disabled={saving}>
                <legend className="campaign-unsub-legend">Rodapé de descadastro</legend>
                <label className="campaign-unsub-label-row">
                  <input
                    type="radio"
                    name="wiz-unsub"
                    checked={useGlobalUnsubscribeConfig}
                    onChange={() => setUseGlobalUnsubscribeConfig(true)}
                  />
                  <span>Usar texto e URL das Configurações</span>
                </label>
                <label className="campaign-unsub-label-row">
                  <input
                    type="radio"
                    name="wiz-unsub"
                    checked={!useGlobalUnsubscribeConfig}
                    onChange={() => setUseGlobalUnsubscribeConfig(false)}
                  />
                  <span>Personalizar texto do rodapé nesta campanha</span>
                </label>
              </fieldset>
            </div>
            {!useGlobalUnsubscribeConfig && (
              <div className="campaign-form-group">
                <label>Template do rodapé</label>
                <textarea
                  value={createUnsubscribeFooterTemplate}
                  onChange={(e) => setCreateUnsubscribeFooterTemplate(e.target.value)}
                  rows={3}
                  disabled={saving}
                />
              </div>
            )}
          </div>

          <aside className="campaign-wizard-card campaign-wizard-summary">
            <h4>Resumo da campanha</h4>
            <div className="campaign-wizard-summary-row">
              <span>Status</span>
              <span className="campaign-status-badge status-rascunho">Rascunho</span>
            </div>
            <div className="campaign-wizard-summary-row">
              <span>Total de leads</span>
              <strong className="lead-count">{leadCount}</strong>
            </div>
            <div className="campaign-wizard-summary-row campaign-wizard-summary-row--attendance">
              <span>Tipo de atendimento</span>
              {attendanceSummaryPill}
            </div>
            <div className="campaign-wizard-summary-row campaign-wizard-summary-row--stack">
              <span className="campaign-wizard-remarketing-label">Prazo para remarketing</span>
              <strong className="campaign-wizard-remarketing-value">
                {organization?.defaultRemarketingMeses != null &&
                Number.isInteger(organization.defaultRemarketingMeses) &&
                organization.defaultRemarketingMeses >= 1
                  ? `${organization.defaultRemarketingMeses} meses`
                  : 'Conforme Configurações'}
              </strong>
              <span className="campaign-wizard-remarketing-hint">Configurado pelo administrador</span>
            </div>
            <div className="campaign-wizard-preview-below campaign-full-message-preview" role="region">
              <label className="campaign-full-message-preview-label">Prévia da mensagem completa</label>
              <pre tabIndex={0}>{createFullMessagePreview}</pre>
            </div>
          </aside>
        </div>
      )}

      {step === 2 && campaignId && (
        <div className="campaign-wizard-layout">
          <CampaignSourceManager
            ref={sourceManagerRef}
            embedded
            variant="wizard"
            lockedCampaignId={campaignId}
            onWizardSelectionTotals={onWizardSelectionTotals}
          />
          <aside className="campaign-wizard-card campaign-wizard-summary campaign-wizard-step2-summary">
            <h4>Resumo da campanha</h4>
            <div className="campaign-wizard-summary-row">
              <span>Status</span>
              <span className="campaign-status-badge status-rascunho">Rascunho</span>
            </div>
            <div className="campaign-wizard-summary-row">
              <span>Total selecionado</span>
              <strong className="lead-count">
                {wizardStep2Totals.total.toLocaleString('pt-BR')}
              </strong>
            </div>
            <div className="campaign-wizard-summary-row campaign-wizard-summary-row--attendance">
              <span>Tipo de atendimento</span>
              {attendanceSummaryPill}
            </div>
            {(wizardStep2Totals.perdidos > 0 ||
              wizardStep2Totals.inativos > 0 ||
              wizardStep2Totals.parceiros > 0 ||
              wizardStep2Totals.csv > 0) && (
              <div className="campaign-wizard-summary-row campaign-wizard-summary-row--wrap">
                <span>Fontes adicionadas</span>
                <div className="campaign-wizard-source-chips">
                  {wizardStep2Totals.perdidos > 0 ? (
                    <span className="campaign-wizard-source-chip">
                      perdidos ({wizardStep2Totals.perdidos.toLocaleString('pt-BR')})
                    </span>
                  ) : null}
                  {wizardStep2Totals.inativos > 0 ? (
                    <span className="campaign-wizard-source-chip">
                      inativos ({wizardStep2Totals.inativos.toLocaleString('pt-BR')})
                    </span>
                  ) : null}
                  {wizardStep2Totals.parceiros > 0 ? (
                    <span className="campaign-wizard-source-chip">
                      parceiros ({wizardStep2Totals.parceiros.toLocaleString('pt-BR')})
                    </span>
                  ) : null}
                  {wizardStep2Totals.csv > 0 ? (
                    <span className="campaign-wizard-source-chip">
                      csv ({wizardStep2Totals.csv.toLocaleString('pt-BR')})
                    </span>
                  ) : null}
                </div>
              </div>
            )}
            <div className="campaign-wizard-summary-divider" />
            <div className="campaign-wizard-summary-row campaign-wizard-summary-row--stack">
              <span className="campaign-wizard-remarketing-label">Prazo para remarketing</span>
              <strong className="campaign-wizard-remarketing-value">
                {organization?.defaultRemarketingMeses != null &&
                Number.isInteger(organization.defaultRemarketingMeses) &&
                organization.defaultRemarketingMeses >= 1
                  ? `${organization.defaultRemarketingMeses} meses`
                  : 'Conforme Configurações'}
              </strong>
              <span className="campaign-wizard-remarketing-hint">Configurado pelo administrador</span>
            </div>
            <p className="campaign-wizard-step2-hint text-muted">
              Ao clicar em <strong>Próximo</strong>, os contactos selecionados são adicionados à campanha (não é
              necessário outro botão).
            </p>
          </aside>
        </div>
      )}

      {step === 3 && (
        <div className="campaign-wizard-layout">
          <div className="campaign-wizard-step3-main">
            <div className="campaign-wizard-card campaign-wizard-card--attendance">
              <h3 className="campaign-wizard-section-heading">Tipo de atendimento</h3>
              <p className="text-muted campaign-wizard-section-lead">
                Define se as respostas dos leads ficam no Kanban para a equipa ou se a IA conduz o funil automaticamente.
              </p>
              <div className="campaign-wizard-attendance-cards" role="group" aria-label="Tipo de atendimento">
                <button
                  type="button"
                  className={`campaign-wizard-attendance-card${createAttendanceMode === 'human_kanban' ? ' is-selected' : ''}`}
                  onClick={() => setCreateAttendanceMode('human_kanban')}
                  disabled={saving}
                >
                  <span className="campaign-wizard-attendance-card-title">Atendimento manual</span>
                  <span className="campaign-wizard-attendance-card-desc">
                    Leads que responderem aparecerão no Kanban para atendimento humano.
                  </span>
                </button>
                <button
                  type="button"
                  className={`campaign-wizard-attendance-card${createAttendanceMode === 'ai_funnel' ? ' is-selected' : ''}`}
                  onClick={() => setCreateAttendanceMode('ai_funnel')}
                  disabled={saving}
                >
                  <span className="campaign-wizard-attendance-card-title">
                    Atendimento por IA
                    <span className="campaign-wizard-ia-pill campaign-wizard-ia-pill--inline">IA</span>
                  </span>
                  <span className="campaign-wizard-attendance-card-desc">
                    A IA conduz a conversa automaticamente através do funil de vendas.
                  </span>
                </button>
              </div>
            </div>
            {/* add-campaign-lead-assignment: USUÁRIOS RESPONSÁVEIS */}
            <div className="campaign-wizard-card">
              <h3 className="campaign-wizard-section-heading" style={{ textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5 }}>
                Usuários responsáveis
              </h3>
              <p className="text-muted" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
                Marque o atendente que vai receber leads inbound dessa campanha.
                Sem marcar: leads vão pro dono da conta WhatsApp (comportamento padrão).
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {orgActiveUsersForAssignment.length === 0 && (
                  <p className="text-muted" style={{ fontSize: 12, gridColumn: '1 / -1' }}>
                    Nenhum usuário com WhatsApp principal configurado. Configure em
                    "Equipa" antes de prosseguir.
                  </p>
                )}
                {orgActiveUsersForAssignment.map((u) => {
                  const checked = createAssignedUserIds.includes(u.usuarioId);
                  return (
                    <label
                      key={u.usuarioId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        cursor: saving ? 'wait' : 'pointer',
                        background: checked ? '#eff6ff' : '#fff',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={saving}
                        onChange={(e) => {
                          setCreateAssignedUserIds((prev) =>
                            e.target.checked
                              ? [...prev.filter((id) => id !== u.usuarioId), u.usuarioId]
                              : prev.filter((id) => id !== u.usuarioId)
                          );
                        }}
                      />
                      <span style={{ fontSize: 13 }}>
                        {u.nome} <span style={{ color: '#64748b', fontSize: 11 }}>({USER_ROLE_LABELS[u.role]})</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {createAssignedUserIds.length > 1 && (
                <p style={{ fontSize: 12, color: '#b91c1c', marginTop: 8 }}>
                  ⚠️ MVP atual aceita só <strong>1 atendente</strong>.
                  Distribuição entre vários (least-busy) virá na próxima versão.
                </p>
              )}
            </div>
            <div className="campaign-wizard-card">
          <p className="text-muted" style={{ marginTop: 0, marginBottom: '1rem', fontSize: 13 }}>
            A conta WhatsApp de envio é a <strong>linha padrão</strong> escolhida no passo 1. Estes valores são
            gravados na campanha e usados no primeiro <strong>Play</strong> no Kanban (pode afinar depois em
            Histórico de Disparos).
          </p>
          <div className="campaign-form-group">
            <label htmlFor="wiz-interval">Intervalo entre envios (minutos)</label>
            <p className="text-muted" style={{ margin: '0 0 0.5rem', fontSize: 12 }}>
              Espaçamento aleatório entre mensagens (1 a N min). Com <strong>1</strong> min, o sistema usa ~20–60
              segundos entre envios.
            </p>
            <input
              id="wiz-interval"
              type="number"
              min={1}
              value={intervaloMinutos}
              onChange={(e) => setIntervaloMinutos(Number(e.target.value))}
              disabled={saving}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="campaign-form-group">
              <label htmlFor="wiz-hi">Hora início</label>
              <input
                id="wiz-hi"
                type="time"
                value={horaInicio}
                onChange={(e) => setHoraInicio(e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="campaign-form-group">
              <label htmlFor="wiz-hf">Hora fim</label>
              <input
                id="wiz-hf"
                type="time"
                value={horaFim}
                onChange={(e) => setHoraFim(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
          <div className="campaign-form-group">
            <label htmlFor="wiz-max">Pausar após N envios (por rodada)</label>
            <p className="text-muted" style={{ margin: '0 0 0.5rem', fontSize: 12 }}>
              Ao atingir este número, a fila pausa até clicar em <strong>Play</strong> de novo (salvo «Disparar até
              o fim» no histórico).
            </p>
            <input
              id="wiz-max"
              type="number"
              min={1}
              value={maxDisparosPorNumero}
              onChange={(e) => setMaxDisparosPorNumero(Number(e.target.value))}
              disabled={saving}
            />
          </div>
            </div>
          </div>
          <aside className="campaign-wizard-card campaign-wizard-summary campaign-wizard-step3-summary">
            <h4>Resumo da campanha</h4>
            <div className="campaign-wizard-summary-row">
              <span>Status</span>
              <span className="campaign-status-badge status-rascunho">Rascunho</span>
            </div>
            <div className="campaign-wizard-summary-row">
              <span>Total de leads</span>
              <strong className="lead-count">{leadCount.toLocaleString('pt-BR')}</strong>
            </div>
            <div className="campaign-wizard-summary-row campaign-wizard-summary-row--attendance">
              <span>Tipo de atendimento</span>
              {attendanceSummaryPill}
            </div>
            <div className="campaign-wizard-summary-divider" />
            <div className="campaign-wizard-summary-row campaign-wizard-summary-row--stack">
              <span className="campaign-wizard-remarketing-label">Prazo para remarketing</span>
              <strong className="campaign-wizard-remarketing-value">
                {organization?.defaultRemarketingMeses != null &&
                Number.isInteger(organization.defaultRemarketingMeses) &&
                organization.defaultRemarketingMeses >= 1
                  ? `${organization.defaultRemarketingMeses} meses`
                  : 'Conforme Configurações'}
              </strong>
              <span className="campaign-wizard-remarketing-hint">Configurado pelo administrador</span>
            </div>
          </aside>
        </div>
      )}

      {step === 4 && (
        <div className="campaign-wizard-review-layout">
          <div className="campaign-wizard-review-stack">
            <div className="campaign-wizard-card campaign-wizard-review-card">
              <h4>Dados da campanha</h4>
              <dl>
                <dt>Nome</dt>
                <dd>{(nome || '').trim() || '—'}</dd>
                <dt>Mensagem</dt>
                <dd className="campaign-wizard-review-msg-dd">
                  {(mensagem || '').trim() || '—'}
                </dd>
              </dl>
            </div>
            <div className="campaign-wizard-card campaign-wizard-review-card campaign-wizard-review-card--compact">
              <h4>Público da campanha</h4>
              <dl>
                <dt>Total de leads</dt>
                <dd>{leadCount.toLocaleString('pt-BR')}</dd>
                <dt>Fontes</dt>
                <dd>{reviewFontesResumo}</dd>
              </dl>
            </div>
            <div className="campaign-wizard-card campaign-wizard-review-card">
              <h4>Configuração do disparo</h4>
              <dl>
                <dt>Tipo de atendimento</dt>
                <dd>
                  {createAttendanceMode === 'ai_funnel' ? (
                    <span className="campaign-wizard-review-ia-automatica">
                      <span aria-hidden>🤖</span> IA automática
                    </span>
                  ) : (
                    'Atendimento manual'
                  )}
                </dd>
                <dt>Conta WhatsApp</dt>
                <dd>
                  {selectableDispatchAccounts.find((a) => a.accountId === createDispatchWhatsappAccountId.trim())
                    ?.nome || createDispatchWhatsappAccountId || '—'}
                </dd>
                <dt>Intervalo entre envios</dt>
                <dd>
                  {intervaloMinutos} min
                  {intervaloMinutos === 1 ? ' (~20–60 s entre envios)' : ''}
                </dd>
                <dt>Horário</dt>
                <dd>
                  {horaInicio} – {horaFim}
                </dd>
                <dt>Máximo de disparos (por rodada)</dt>
                <dd>{maxDisparosPorNumero}</dd>
                <dt>Responsáveis</dt>
                <dd>
                  {createPrincipalBlock && createPrincipalSelectedMember
                    ? createPrincipalSelectedMember.nome
                    : '—'}
                </dd>
              </dl>
            </div>
            <div className="campaign-wizard-card campaign-wizard-review-card campaign-wizard-review-card--compact">
              <h4>Regras automáticas</h4>
              <p className="campaign-wizard-review-rules-text">
                <strong>Prazo para remarketing:</strong>{' '}
                {organization?.defaultRemarketingMeses != null &&
                Number.isInteger(organization.defaultRemarketingMeses) &&
                organization.defaultRemarketingMeses >= 1
                  ? `${organization.defaultRemarketingMeses} meses`
                  : 'Conforme Configurações da organização'}
                . Leads sem sucesso poderão voltar a campanhas após esse período.
              </p>
            </div>
          </div>
          <div className="campaign-wizard-review-preview-wrap">
            <div className="campaign-wizard-card campaign-wizard-review-card campaign-wizard-review-preview-card">
              <h4>Prévia da mensagem</h4>
              <pre className="campaign-wizard-review-preview-pre" tabIndex={0}>
                {(mensagem || '').trim() || '(Vazio)'}
              </pre>
              <p className="campaign-wizard-review-preview-foot text-muted">
                Texto principal do disparo; blocos «Ligue para» e rodapé aparecem no envio real.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="campaign-wizard-actions">
        <button
          type="button"
          className="btn btn-outline"
          disabled={saving}
          onClick={() => {
            if (step === 1) onClose();
            else goBack();
          }}
        >
          Voltar
        </button>
        <div className="campaign-wizard-actions-right">
          {step < 4 ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || (step === 2 && wizardStep2Totals.total === 0)}
              onClick={() => void goNext()}
            >
              Próximo
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void goNext()}>
              {saving ? 'Ativando…' : 'Ativar campanha'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
